/**
 * CONSAI Business App — Multi-step Questionnaire
 * Redesigned per Stitch-generated dark design system
 * 4 screens: Basic Info → Foundation Questions → AI Questions → Generating Report
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, ChevronDown, Sparkles, Loader2, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Design tokens ──────────────────────────────────────────── */
const T = {
  bg: '#0A0A0A',
  surface: '#131313',
  surfaceLow: '#1C1B1B',
  border: '#2E2D2D',
  borderOut: '#484646',
  input: '#0E0E0E',
  text: '#F4F4F5',
  sub: '#C7C6C6',
  white: '#ffffff',
  black: '#000000',
};

/* ── Keyframe animations injected once ─────────────────────── */
const ANIM_STYLES = `
  @keyframes ringPulse {
    0%,100%{transform:scale(0.95);opacity:0.2;border-width:2px}
    50%{transform:scale(1.05);opacity:0.6;border-width:1px}
  }
  @keyframes softPulse {
    0%,100%{opacity:1} 50%{opacity:0.55}
  }
  @keyframes spin {
    from{transform:rotate(0deg)} to{transform:rotate(360deg)}
  }
`;

/* ── Shared: Dark Input ─────────────────────────────────────── */
const DInput = ({ label, value, onChange, placeholder, testId, type = 'text' }) => (
  <div className="space-y-2">
    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.sub }}>
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testId}
      style={{
        width: '100%', background: T.input, border: `1px solid ${T.border}`, borderRadius: 8,
        padding: '13px 16px', color: T.text, fontSize: 15, outline: 'none', transition: 'border-color 0.2s',
        fontFamily: 'inherit'
      }}
      onFocus={e => e.target.style.borderColor = T.white}
      onBlur={e => e.target.style.borderColor = T.border}
    />
  </div>
);

/* ── Shared: Dark Select ───────────────────────────────────── */
const DSelect = ({ label, value, onChange, options, placeholder, testId }) => (
  <div className="space-y-2">
    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.sub }}>
      {label}
    </label>
    <div style={{ position: 'relative' }}>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        data-testid={testId}
        style={{
          width: '100%', background: T.input, border: `1px solid ${T.border}`, borderRadius: 8,
          padding: '13px 40px 13px 16px', color: value ? T.text : T.sub, fontSize: 15,
          outline: 'none', appearance: 'none', transition: 'border-color 0.2s', fontFamily: 'inherit', cursor: 'pointer'
        }}
        onFocus={e => e.target.style.borderColor = T.white}
        onBlur={e => e.target.style.borderColor = T.border}
      >
        <option value="" disabled style={{ background: T.surface }}>{placeholder}</option>
        {options.map(o => (
          <option key={o.value} value={o.value} style={{ background: T.surface }}>{o.label}</option>
        ))}
      </select>
      <ChevronDown style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: T.sub, pointerEvents: 'none', width: 16, height: 16 }} />
    </div>
  </div>
);

/* ── Shared: Sticky Glass Header ───────────────────────────── */
const QHeader = ({ step, onBack, language }) => {
  const steps = [
    language === 'hi' ? 'बेसिक जानकारी' : 'Basic Info',
    language === 'hi' ? 'फाउंडेशन' : 'Foundation',
    language === 'hi' ? 'AI प्रश्न' : 'AI Questions',
  ];
  return (
    <header style={{ position: 'sticky', top: 0, zIndex: 50, borderBottom: `1px solid ${T.border}`, background: 'rgba(10,10,10,0.9)', backdropFilter: 'blur(14px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', height: 64, maxWidth: 1280, margin: '0 auto' }}>
        {/* Left */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onBack}
            data-testid="back-btn"
            style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, background: 'transparent', border: 'none', cursor: 'pointer', transition: 'background 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <ArrowLeft style={{ color: T.sub, width: 18, height: 18 }} />
          </button>
          <span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>CONSAI</span>
          <span style={{ width: 1, height: 16, background: T.borderOut }} />
          <span style={{ fontSize: 13, color: T.sub, display: 'none' }} className="md-hidden">
            {language === 'hi' ? 'AI बिज़नेस रिपोर्ट' : 'AI Business Report'}
          </span>
        </div>
        {/* Desktop stepper */}
        <div className="hidden md:flex items-center gap-6">
          {steps.map((label, i) => {
            const n = i + 1;
            const active = n === step;
            const done = n < step;
            return (
              <div key={`step-dot-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {i > 0 && <span style={{ width: 28, height: 1, background: T.border }} />}
                <div style={{
                  width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 700, flexShrink: 0,
                  background: active || done ? T.white : 'transparent',
                  border: active || done ? 'none' : `1px solid ${T.borderOut}`,
                  color: active || done ? T.black : T.sub,
                }}>
                  {done ? '✓' : n}
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: active ? T.text : T.sub, opacity: active ? 1 : 0.45 }}>
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      {/* Progress bar */}
      <div style={{ height: 2, background: '#1A1A1A' }}>
        <div style={{ height: '100%', background: T.white, width: `${Math.round((step / 3) * 100)}%`, transition: 'width 0.5s ease' }} />
      </div>
    </header>
  );
};

/* ══════════════════════════════════════════════════════════════
   SCREEN 1 — Basic Info
═══════════════════════════════════════════════════════════════ */
const BasicInfoStep = ({ basicInfo, setBasicInfo, config, onContinue, submitting, language }) => {
  const bizTypes = (config?.business_types || []).map(bt => ({
    value: bt.value, label: language === 'hi' ? bt.label_hi : bt.label_en
  }));
  const revRanges = (config?.revenue_ranges || []).map(r => ({
    value: r.value, label: language === 'hi' ? r.label_hi : r.label_en
  }));

  return (
    <main style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '40px 20px 60px', minHeight: 'calc(100vh - 66px)' }}>
      <div style={{ width: '100%', maxWidth: 640 }}>
        {/* Card */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '32px 28px', overflow: 'hidden' }}>
          <div style={{ marginBottom: 28 }}>
            <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.sub, marginBottom: 8 }}>
              {language === 'hi' ? 'चरण 1 — कोर फंडामेंटल्स' : 'STEP 1 — CORE FUNDAMENTALS'}
            </p>
            <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: T.text, lineHeight: 1.2, marginBottom: 8 }}>
              {language === 'hi' ? 'बिज़नेस की बेसिक जानकारी' : 'Business Entity Details'}
            </h1>
            <p style={{ fontSize: 15, color: T.sub, lineHeight: 1.65 }}>
              {language === 'hi'
                ? 'AI इंजन को कैलिब्रेट करने के लिए बुनियादी जानकारी दें।'
                : 'Provide foundational business context to calibrate the AI analysis engine.'}
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <DSelect
              label={language === 'hi' ? 'बिज़नेस का प्रकार' : 'Business Type'}
              value={basicInfo.business_type}
              onChange={v => setBasicInfo({ ...basicInfo, business_type: v, custom_business_type: '' })}
              options={bizTypes}
              placeholder={language === 'hi' ? 'प्रकार चुनें' : 'Select business type'}
              testId="business-type-select"
            />

            {basicInfo.business_type === 'other' && (
              <DInput
                label={language === 'hi' ? 'बिज़नेस बताएं' : 'Specify Business Type'}
                value={basicInfo.custom_business_type}
                onChange={e => setBasicInfo({ ...basicInfo, custom_business_type: e.target.value })}
                placeholder={language === 'hi' ? 'जैसे: हैंडीक्राफ्ट' : 'e.g. Handicrafts, Event Planning'}
                testId="custom-business-type-input"
              />
            )}

            <DInput
              label={language === 'hi' ? 'लोकेशन (शहर, राज्य)' : 'Location (City & State)'}
              value={basicInfo.location}
              onChange={e => setBasicInfo({ ...basicInfo, location: e.target.value })}
              placeholder={language === 'hi' ? 'जैसे: जयपुर, राजस्थान' : 'e.g. Jaipur, Rajasthan'}
              testId="location-input"
            />

            {/* Revenue buttons */}
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.sub, marginBottom: 8 }}>
                {language === 'hi' ? 'वार्षिक राजस्व अनुमान (₹)' : 'Annual Revenue Estimate (₹)'}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                {revRanges.map(r => {
                  const active = basicInfo.annual_revenue_range === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setBasicInfo({ ...basicInfo, annual_revenue_range: r.value })}
                      data-testid={`revenue-${r.value}`}
                      style={{
                        padding: '10px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        transition: 'all 0.15s', outline: 'none', fontFamily: 'inherit',
                        background: active ? T.white : 'transparent',
                        border: `1px solid ${active ? T.white : T.border}`,
                        color: active ? T.black : T.sub,
                      }}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div style={{ marginTop: 36, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <button
              onClick={onContinue}
              disabled={submitting}
              data-testid="next-btn"
              style={{
                width: '100%', background: submitting ? '#2A2A2A' : T.white, color: submitting ? T.sub : T.black,
                fontWeight: 700, fontSize: 14, letterSpacing: '0.06em', textTransform: 'uppercase',
                padding: '14px 32px', borderRadius: 8, border: 'none', cursor: submitting ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'opacity 0.2s', fontFamily: 'inherit'
              }}
            >
              {submitting ? <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} /> : null}
              {language === 'hi' ? 'आगे बढ़ें' : 'CONTINUE'}
              {!submitting && <ArrowRight style={{ width: 16, height: 16 }} />}
            </button>
            <p style={{ fontSize: 12, color: T.sub, opacity: 0.55 }}>
              {language === 'hi' ? 'चरण 1 / 3 — प्रगति स्वतः सहेजी जाती है' : 'Step 1 of 3 — Progress saved automatically'}
            </p>
          </div>
        </div>
      </div>

      {/* Bg glow */}
      <div style={{ position: 'fixed', top: '-10%', right: '-5%', width: '40%', height: '40%', background: 'rgba(255,255,255,0.03)', filter: 'blur(120px)', borderRadius: '50%', pointerEvents: 'none', zIndex: -1 }} />
    </main>
  );
};

/* ══════════════════════════════════════════════════════════════
   SCREEN 2 — Foundation Questions
═══════════════════════════════════════════════════════════════ */
const FoundationStep = ({ foundationAnswers, setFoundationAnswers, config, onSubmit, submitting, generatingQuestions, language }) => {
  const questions = config?.foundation_questions || [];
  const [visibleCount, setVisibleCount] = useState(6);
  const answered = foundationAnswers.filter(a => a.trim()).length;
  const pct = questions.length ? Math.round((answered / questions.length) * 100) : 0;

  return (
    <div style={{ minHeight: '100vh', paddingBottom: 96 }}>
      <main style={{ display: 'flex', justifyContent: 'center', padding: '32px 20px 24px' }}>
        <div style={{ width: '100%', maxWidth: 800 }}>
          {/* Title row */}
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 10 }}>
            <div>
              <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.sub, marginBottom: 4 }}>
                {language === 'hi' ? 'STEP 2 — फाउंडेशन विश्लेषण' : 'STEP 2 — FOUNDATION ANALYSIS'}
              </p>
              <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: T.text }}>
                {language === 'hi' ? `${questions.length} बिज़नेस प्रश्न` : `${questions.length} Business Questions`}
              </h1>
            </div>
            <span style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{pct}%</span>
          </div>

          {/* Progress bar */}
          <div style={{ height: 2, background: T.border, borderRadius: 99, marginBottom: 24, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: T.white, width: `${pct}%`, transition: 'width 0.3s', borderRadius: 99 }} />
          </div>

          {/* Questions card */}
          <div style={{ background: T.surfaceLow, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
            {questions.slice(0, visibleCount).map((question, index) => (
              <div key={`question-${index}`} style={{ borderBottom: index < Math.min(visibleCount, questions.length) - 1 ? `1px solid ${T.border}` : 'none' }}>
                <div style={{ display: 'flex', gap: 16, padding: 24 }}>
                  {/* Number circle */}
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', border: `1px solid ${T.borderOut}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    fontSize: 12, fontWeight: 700, color: T.text
                  }}>
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <p style={{ fontSize: 15, color: T.text, lineHeight: 1.65 }}>{question}</p>
                    <textarea
                      value={foundationAnswers[index]}
                      onChange={e => {
                        const arr = [...foundationAnswers];
                        arr[index] = e.target.value;
                        setFoundationAnswers(arr);
                      }}
                      placeholder={language === 'hi' ? 'अपना उत्तर लिखें...' : 'Write your answer...'}
                      rows={2}
                      data-testid={`foundation-q-${index}`}
                      style={{
                        width: '100%', background: T.input, border: `1px solid ${T.border}`, borderRadius: 8,
                        padding: 14, color: T.text, fontSize: 14, resize: 'none', outline: 'none',
                        transition: 'border-color 0.2s', minHeight: 76, fontFamily: 'inherit',
                        lineHeight: 1.5
                      }}
                      onFocus={e => e.target.style.borderColor = T.white}
                      onBlur={e => e.target.style.borderColor = T.border}
                    />
                  </div>
                </div>
              </div>
            ))}

            {visibleCount < questions.length && (
              <div style={{ padding: '14px 24px', textAlign: 'center', background: 'rgba(14,14,14,0.5)', borderTop: `1px solid ${T.border}` }}>
                <button
                  onClick={() => setVisibleCount(questions.length)}
                  style={{ fontSize: 13, fontWeight: 600, color: T.sub, background: 'none', border: 'none', cursor: 'pointer', transition: 'color 0.2s', fontFamily: 'inherit', padding: '4px 0' }}
                  onMouseEnter={e => e.target.style.color = T.white}
                  onMouseLeave={e => e.target.style.color = T.sub}
                >
                  {language === 'hi'
                    ? `${questions.length - visibleCount} और प्रश्न दिखाएं`
                    : `Load ${questions.length - visibleCount} more questions`}
                </button>
              </div>
            )}
          </div>

          {/* AI badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: 'rgba(255,255,255,0.03)', border: `1px solid ${T.border}`, borderRadius: 8 }}>
            <Sparkles style={{ width: 14, height: 14, color: T.sub }} />
            <p style={{ fontSize: 12, color: T.sub }}>
              {language === 'hi' ? 'AI आपके जवाबों का विश्लेषण करके पर्सनलाइज्ड फॉलो-अप प्रश्न बनाएगा।' : 'AI will analyse your answers and generate personalised follow-up questions.'}
            </p>
          </div>
        </div>
      </main>

      {/* Sticky footer */}
      <footer style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50,
        background: T.surface, borderTop: `1px solid ${T.border}`, padding: '14px 20px',
        display: 'flex', justifyContent: 'center'
      }}>
        <div style={{ width: '100%', maxWidth: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <button
            style={{
              padding: '10px 24px', border: `1px solid ${T.border}`, borderRadius: 8,
              background: 'transparent', color: T.text, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              letterSpacing: '0.04em', fontFamily: 'inherit', transition: 'background 0.2s'
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            {language === 'hi' ? 'SAVE PROGRESS' : 'SAVE PROGRESS'}
          </button>
          <button
            onClick={onSubmit}
            disabled={submitting || generatingQuestions}
            data-testid="generate-questions-btn"
            style={{
              padding: '10px 28px', background: (submitting || generatingQuestions) ? '#2A2A2A' : T.white,
              color: (submitting || generatingQuestions) ? T.sub : T.black,
              fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              borderRadius: 8, border: 'none', cursor: (submitting || generatingQuestions) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8, transition: 'opacity 0.2s', fontFamily: 'inherit'
            }}
          >
            {(submitting || generatingQuestions)
              ? <><Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />{language === 'hi' ? 'जनरेट हो रहा है...' : 'Generating...'}</>
              : <><Sparkles style={{ width: 14, height: 14 }} />{language === 'hi' ? 'AI प्रश्न जनरेट करें' : 'GENERATE AI QUESTIONS'}<ArrowRight style={{ width: 14, height: 14 }} /></>
            }
          </button>
        </div>
      </footer>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   SCREEN 3 — AI-Generated Questions (MCQ)
═══════════════════════════════════════════════════════════════ */
const AIQuestionsStep = ({ generatedQuestions, generatedAnswers, onAnswerChange, onNotesChange, onSubmit, submitting, language }) => {
  const [openNotes, setOpenNotes] = useState({});

  const CAT_COLORS = {
    operations: '#A78BFA', finance: '#34D399', marketing: '#60A5FA',
    hr: '#F59E0B', strategy: '#FB7185', default: '#94A3B8'
  };
  const catColor = c => CAT_COLORS[(c || '').toLowerCase()] || CAT_COLORS.default;

  return (
    <div style={{ minHeight: '100vh', paddingBottom: 96 }}>
      <main style={{ display: 'flex', justifyContent: 'center', padding: '32px 20px 24px' }}>
        <div style={{ width: '100%', maxWidth: 800 }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
            <div>
              <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.sub, marginBottom: 4 }}>
                {language === 'hi' ? 'STEP 3 — CONTEXT ANALYSIS' : 'STEP 3 — CONTEXT ANALYSIS'}
              </p>
              <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: T.text }}>
                {language === 'hi' ? 'AI-जनरेटेड प्रश्न' : 'AI-Generated Questions'}
              </h1>
            </div>
            <div style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.06)', border: `1px solid ${T.border}`, borderRadius: 6, flexShrink: 0, marginTop: 4 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: T.white, letterSpacing: '0.1em' }}>100% COMPLETE</span>
            </div>
          </div>
          <p style={{ fontSize: 14, color: T.sub, lineHeight: 1.65, marginBottom: 28 }}>
            {language === 'hi'
              ? 'आपके जवाबों के आधार पर AI ने ये विशेष प्रश्न तैयार किए हैं। कृपया इन्हें वैलिडेट करें।'
              : 'Based on your previous inputs, our AI has identified critical strategic points. Please validate the following hypotheses.'}
          </p>

          {/* Question cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {generatedQuestions.map((q, index) => (
              <div
                key={q.question || `gen-q-${index}`}
                data-testid={`ai-question-${index}`}
                style={{ background: T.surfaceLow, border: `1px solid ${T.border}`, borderRadius: 12, padding: '20px 20px 16px' }}
              >
                {/* Category + question number + title */}
                <div style={{ display: 'flex', gap: 14, marginBottom: 16, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', background: T.white, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700, color: T.black
                  }}>
                    {q.question_number || index + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    {q.category && (
                      <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: 4, marginBottom: 8,
                        background: 'rgba(255,255,255,0.06)', border: `1px solid ${T.border}`,
                        fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                        color: catColor(q.category)
                      }}>
                        {q.category}
                      </span>
                    )}
                    <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, lineHeight: 1.5 }}>{q.question}</h3>
                  </div>
                </div>

                {/* MCQ 2-col grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 8, marginBottom: 12 }}>
                  {q.options?.map(opt => {
                    const sel = generatedAnswers[index]?.selected_option === opt.key;
                    return (
                      <button
                        key={opt.key}
                        onClick={() => onAnswerChange(index, opt.key, opt.text)}
                        data-testid={`option-${index}-${opt.key}`}
                        style={{
                          padding: '12px 14px', borderRadius: 8, textAlign: 'left', cursor: 'pointer',
                          transition: 'all 0.15s', border: `1px solid ${sel ? T.white : T.borderOut}`,
                          background: sel ? 'rgba(255,255,255,0.04)' : T.input,
                          boxShadow: sel ? 'inset 3px 0 0 0 #fff' : 'none',
                          display: 'flex', alignItems: 'flex-start', gap: 10, fontFamily: 'inherit'
                        }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 700, color: sel ? T.white : T.sub, flexShrink: 0, marginTop: 1 }}>
                          {opt.key.toUpperCase()}.
                        </span>
                        <span style={{ fontSize: 13, color: sel ? T.text : T.sub, lineHeight: 1.5 }}>
                          {opt.text}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Notes toggle */}
                {!openNotes[index] ? (
                  <button
                    onClick={() => setOpenNotes(p => ({ ...p, [index]: true }))}
                    style={{ fontSize: 12, color: T.sub, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.target.style.color = T.white}
                    onMouseLeave={e => e.target.style.color = T.sub}
                  >
                    + {language === 'hi' ? 'नोट जोड़ें' : 'Add a note'}
                  </button>
                ) : (
                  <textarea
                    value={generatedAnswers[index]?.notes || ''}
                    onChange={e => onNotesChange(index, e.target.value)}
                    placeholder={language === 'hi' ? 'अतिरिक्त संदर्भ...' : 'Additional context...'}
                    rows={2}
                    style={{
                      width: '100%', background: T.input, border: `1px solid ${T.border}`, borderRadius: 8,
                      padding: '10px 14px', color: T.text, fontSize: 13, resize: 'none', outline: 'none',
                      transition: 'border-color 0.2s', fontFamily: 'inherit'
                    }}
                    onFocus={e => e.target.style.borderColor = T.white}
                    onBlur={e => e.target.style.borderColor = T.border}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Sticky footer */}
      <footer style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50,
        background: T.surface, borderTop: `1px solid ${T.border}`, padding: '14px 20px',
        display: 'flex', justifyContent: 'center'
      }}>
        <div style={{ width: '100%', maxWidth: 800, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <button
            onClick={onSubmit}
            disabled={submitting}
            data-testid="submit-questionnaire-btn"
            style={{
              padding: '12px 36px', background: submitting ? '#2A2A2A' : T.white, color: submitting ? T.sub : T.black,
              fontSize: 14, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              borderRadius: 8, border: 'none', cursor: submitting ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8, transition: 'opacity 0.2s', fontFamily: 'inherit'
            }}
          >
            {submitting
              ? <><Loader2 style={{ width: 15, height: 15, animation: 'spin 1s linear infinite' }} />{language === 'hi' ? 'सबमिट हो रहा है...' : 'Submitting...'}</>
              : <><Check style={{ width: 15, height: 15 }} />{language === 'hi' ? 'रिपोर्ट जनरेट करें' : 'GENERATE REPORT'}</>
            }
          </button>
        </div>
      </footer>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   SCREEN 4 — Generating Report (Loading State)
═══════════════════════════════════════════════════════════════ */
const GeneratingScreen = ({ language }) => {
  const [progress, setProgress] = useState(6);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const iv = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) { clearInterval(iv); return prev; }
        const next = prev + (Math.random() * 3 + 1);
        if (next > 28 && activeStep < 1) setActiveStep(1);
        if (next > 60 && activeStep < 2) setActiveStep(2);
        return Math.min(next, 90);
      });
    }, 900);
    return () => clearInterval(iv);
  }, []);

  const steps = [
    {
      label: language === 'hi' ? 'मार्केट वेक्टर विश्लेषण' : 'Analysing Market Vectors',
      sub: language === 'hi' ? 'रियल-टाइम प्रतिस्पर्धी डेटा सिंथेसाइज़...' : 'Synthesizing real-time competitive data...',
    },
    {
      label: language === 'hi' ? 'रणनीतिक मॉडल बना रहे हैं' : 'Building Strategic Model',
      sub: language === 'hi' ? 'वेक्टर पूरा होने का इंतजार' : 'Awaiting vector completion',
    },
    {
      label: language === 'hi' ? 'एग्जीक्यूटिव सारांश लिख रहे हैं' : 'Writing Executive Summary',
      sub: language === 'hi' ? 'नेचुरल लैंग्वेज इनसाइट जनरेट हो रही हैं' : 'Generating natural language insights',
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <style>{ANIM_STYLES}</style>

      {/* Header */}
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, height: 64,
        background: 'rgba(14,14,14,0.9)', backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${T.borderOut}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>CONSAI</span>
          <span style={{
            padding: '2px 8px', background: '#2A2A2A', border: `1px solid ${T.borderOut}`,
            borderRadius: 4, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: T.sub, textTransform: 'uppercase'
          }}>
            ENTERPRISE
          </span>
        </div>
      </header>

      {/* Main */}
      <main style={{
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '80px 20px 100px', position: 'relative', zIndex: 10
      }}>
        <div style={{ width: '100%', maxWidth: 520, textAlign: 'center' }}>
          {/* Animated ring */}
          <div style={{ position: 'relative', width: 160, height: 160, margin: '0 auto 36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              border: '2px solid rgba(255,255,255,0.45)',
              animation: 'ringPulse 3s cubic-bezier(0.4,0,0.6,1) infinite'
            }} />
            <div style={{
              position: 'absolute', inset: 18, borderRadius: '50%',
              border: '1px solid rgba(255,255,255,0.1)',
              animation: 'ringPulse 3s cubic-bezier(0.4,0,0.6,1) 1.1s infinite'
            }} />
            {/* Central icon */}
            <div style={{
              width: 64, height: 64, borderRadius: '50%', background: '#2A2A2A',
              border: `1px solid ${T.borderOut}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 20, animation: 'softPulse 2s ease-in-out infinite'
            }}>
              <Zap style={{ width: 26, height: 26, color: T.white }} />
            </div>
          </div>

          <h1 style={{ fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 10 }}>
            {language === 'hi' ? 'आपका बिज़नेस विश्लेषण हो रहा है' : 'Analysing your business'}
            <span style={{ color: T.sub }}>...</span>
          </h1>
          <p style={{ fontSize: 14, color: T.sub, lineHeight: 1.65, maxWidth: 420, margin: '0 auto 32px' }}>
            {language === 'hi'
              ? 'AI इंजन इंडस्ट्री बेंचमार्क के साथ आपका डेटा मिला रहा है।'
              : 'Our neural engine is cross-referencing industry benchmarks with your business data to identify high-impact growth levers.'}
          </p>

          {/* Steps */}
          <div style={{ maxWidth: 440, margin: '0 auto 32px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {steps.map((s, i) => {
              const isActive = i === activeStep;
              const isDone = i < activeStep;
              return (
                <div key={s.title || `step-${i}`} style={{
                  borderLeft: `2px solid ${isActive || isDone ? T.white : '#3A3939'}`,
                  paddingLeft: 20, paddingTop: 6, paddingBottom: 6,
                  opacity: isDone ? 0.7 : isActive ? 1 : 0.35,
                  transition: 'all 0.5s ease'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <p style={{ fontSize: 14, fontWeight: 600, color: isActive ? T.text : T.sub, margin: 0 }}>{s.label}</p>
                      <p style={{ fontSize: 12, color: T.sub, opacity: 0.7, margin: '2px 0 0' }}>{s.sub}</p>
                    </div>
                    <span style={{ fontSize: 16, color: isDone ? T.sub : isActive ? T.white : 'transparent', marginLeft: 12 }}>
                      {isDone ? '✓' : isActive ? '◎' : '○'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Progress bar */}
          <div style={{ maxWidth: 440, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: T.sub }}>{language === 'hi' ? 'वर्तमान प्रगति' : 'Current Progress'}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{Math.round(progress)}%</span>
            </div>
            <div style={{ height: 4, background: '#2A2A2A', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{
                height: '100%', background: T.white, width: `${progress}%`, borderRadius: 99,
                transition: 'width 0.8s ease', boxShadow: '0 0 8px rgba(255,255,255,0.3)'
              }} />
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50, height: 68,
        background: '#0E0E0E', borderTop: `1px solid ${T.borderOut}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.white, animation: 'softPulse 2s ease-in-out infinite', boxShadow: '0 0 5px #fff' }} />
          <span style={{ fontSize: 12, color: T.sub }}>{language === 'hi' ? 'सिस्टम स्थिति: सामान्य' : 'System Status: Nominal'}</span>
        </div>
        <button style={{
          padding: '8px 24px', border: `1px solid ${T.borderOut}`, borderRadius: 8,
          background: 'transparent', color: T.text, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
        }}>
          {language === 'hi' ? 'विश्लेषण रद्द करें' : 'Cancel Analysis'}
        </button>
      </footer>

      {/* Bg glows */}
      <div style={{ position: 'fixed', top: '-5%', left: '-5%', width: '40%', height: '40%', background: 'rgba(255,255,255,0.025)', filter: 'blur(110px)', borderRadius: '50%', pointerEvents: 'none', zIndex: 0 }} />
      <div style={{ position: 'fixed', bottom: '-5%', right: '-5%', width: '30%', height: '30%', background: 'rgba(255,255,255,0.015)', filter: 'blur(100px)', borderRadius: '50%', pointerEvents: 'none', zIndex: 0 }} />
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const Questionnaire = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [config, setConfig] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);

  const [basicInfo, setBasicInfo] = useState({
    business_type: '', custom_business_type: '', location: '', annual_revenue_range: ''
  });
  const [foundationAnswers, setFoundationAnswers] = useState(Array(10).fill(''));
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [generatedAnswers, setGeneratedAnswers] = useState([]);

  useEffect(() => {
    fetchConfig();
    fetchStatus();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/questionnaire/config`, { credentials: 'include' });
      setConfig(await res.json());
    } catch {
      toast.error(language === 'hi' ? 'कॉन्फिग लोड त्रुटि' : 'Error loading config');
    }
  };

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/questionnaire/status`, { credentials: 'include' });
      if (res.status === 403) {
        toast.error(language === 'hi' ? 'पहले रिपोर्ट खरीदें' : 'Please purchase a report first');
        navigate('/payment?type=report');
        return;
      }
      const data = await res.json();
      setQuestionnaire(data);
      if (data.basic_info) setBasicInfo(data.basic_info);
      if (data.foundation_answers) setFoundationAnswers(data.foundation_answers);
      if (data.generated_questions) setGeneratedQuestions(data.generated_questions);
      if (data.generated_answers) setGeneratedAnswers(data.generated_answers);
      if (data.status === 'completed') navigate('/report');
      else if (data.status === 'questions_generated' && data.generated_questions) setStep(3);
      else if (data.status === 'foundation_done') setStep(2);
    } catch {
      toast.error(language === 'hi' ? 'स्थिति लोड त्रुटि' : 'Error loading status');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBasicInfo = async () => {
    const bizType = basicInfo.business_type === 'other' ? basicInfo.custom_business_type : basicInfo.business_type;
    if (!bizType || !basicInfo.location || !basicInfo.annual_revenue_range) {
      toast.error(language === 'hi' ? 'सभी फ़ील्ड भरें' : 'Please fill all fields');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/questionnaire/basic-info`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ business_type: bizType, location: basicInfo.location, annual_revenue_range: basicInfo.annual_revenue_range })
      });
      if (res.ok) setStep(2);
      else throw new Error();
    } catch {
      toast.error(language === 'hi' ? 'सहेजने में त्रुटि' : 'Error saving');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveFoundationAnswers = async () => {
    if (foundationAnswers.some(a => !a.trim())) {
      toast.error(language === 'hi' ? 'सभी प्रश्नों का उत्तर दें' : 'Please answer all questions');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/questionnaire/foundation-answers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ answers: foundationAnswers })
      });
      if (res.ok) {
        setGeneratingQuestions(true);
        setSubmitting(false);
        const r2 = await fetch(`${API_URL}/api/questionnaire/generate-questions`, { method: 'POST', credentials: 'include' });
        if (r2.ok) {
          const d = await r2.json();
          setGeneratedQuestions(d.questions);
          setGeneratedAnswers(d.questions.map((q, i) => ({ question_number: q.question_number || i + 1, question: q.question, selected_option: '', selected_text: '', notes: '' })));
          setStep(3);
        } else throw new Error();
      } else throw new Error();
    } catch {
      toast.error(language === 'hi' ? 'प्रश्न जनरेट त्रुटि' : 'Error generating questions');
    } finally {
      setSubmitting(false);
      setGeneratingQuestions(false);
    }
  };

  const handleAnswerChange = (index, option, text) => {
    const arr = [...generatedAnswers];
    arr[index] = { ...arr[index], selected_option: option, selected_text: text };
    setGeneratedAnswers(arr);
  };

  const handleNotesChange = (index, notes) => {
    const arr = [...generatedAnswers];
    arr[index] = { ...arr[index], notes };
    setGeneratedAnswers(arr);
  };

  const handleSubmitQuestionnaire = async () => {
    if (generatedAnswers.some(a => !a.selected_option)) {
      toast.error(language === 'hi' ? 'सभी प्रश्नों का उत्तर दें' : 'Please answer all questions');
      return;
    }
    setGeneratingReport(true);
    try {
      await fetch(`${API_URL}/api/questionnaire/save-answers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(generatedAnswers)
      });
      const res = await fetch(`${API_URL}/api/questionnaire/submit`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        toast.success(language === 'hi' ? 'रिपोर्ट जनरेट हो रही है!' : 'Report generating!');
        navigate('/report');
      } else throw new Error();
    } catch {
      toast.error(language === 'hi' ? 'सबमिट त्रुटि' : 'Error submitting');
      setGeneratingReport(false);
    }
  };

  /* ── Render states ─── */
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{ANIM_STYLES}</style>
        <Loader2 style={{ width: 32, height: 32, color: T.sub, animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (generatingReport) return <GeneratingScreen language={language} />;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <style>{ANIM_STYLES}</style>
      <QHeader step={step} onBack={() => (step > 1 ? setStep(step - 1) : navigate('/dashboard'))} language={language} />

      {step === 1 && (
        <BasicInfoStep
          basicInfo={basicInfo} setBasicInfo={setBasicInfo}
          config={config} onContinue={handleSaveBasicInfo}
          submitting={submitting} language={language}
        />
      )}
      {step === 2 && (
        <FoundationStep
          foundationAnswers={foundationAnswers} setFoundationAnswers={setFoundationAnswers}
          config={config} onSubmit={handleSaveFoundationAnswers}
          submitting={submitting} generatingQuestions={generatingQuestions} language={language}
        />
      )}
      {step === 3 && (
        <AIQuestionsStep
          generatedQuestions={generatedQuestions} generatedAnswers={generatedAnswers}
          onAnswerChange={handleAnswerChange} onNotesChange={handleNotesChange}
          onSubmit={handleSubmitQuestionnaire} submitting={false} language={language}
        />
      )}
    </div>
  );
};

export default Questionnaire;
